import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/components/site/WeShape.css";

const CAPABILITIES = [
  { word: "Architecture", testid: "architecture" },
  { word: "Interiors", testid: "interiors" },
  { word: "Styling", testid: "styling" },
  { word: "Experiences", testid: "experiences" },
];

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export default function WeShape({ id = "we-shape" }) {
  const reducedMotion = useReducedMotion();
  const journeyRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 881px)").matches
      : true
  );

  // Temporary scroll cue — appears on entry and reappears briefly each
  // time the user pauses while the reveal isn't visibly underway. Only
  // permanently dismissed once WE/SHAPE have clearly split.
  const [cueVisible, setCueVisible] = useState(false);
  const cueDismissedRef = useRef(false);
  const cueArmedOnceRef = useRef(false);
  const cueShowTimerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 881px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Scroll-driven progress (0 → 1 across the pinned journey).
  // Runs on BOTH desktop and mobile — only reduced motion falls
  // back to the static composition.
  useEffect(() => {
    if (reducedMotion) return;
    const el = journeyRef.current;
    if (!el) return;
    let rafId = null;

    // Threshold of split progress past which we consider the reveal
    // "visibly underway" and the cue is dismissed for good.
    const REVEAL_THRESHOLD = 0.35;

    const armReshow = () => {
      if (cueShowTimerRef.current) clearTimeout(cueShowTimerRef.current);
      // First entry: 1.0s. Subsequent re-entries (after a small pause):
      // 1.3s so it feels like a thoughtful return, not a flicker.
      const delay = cueArmedOnceRef.current ? 1300 : 1000;
      cueShowTimerRef.current = setTimeout(() => {
        if (!cueDismissedRef.current) {
          setCueVisible(true);
          cueArmedOnceRef.current = true;
        }
      }, delay);
    };

    const clearReshow = () => {
      if (cueShowTimerRef.current) {
        clearTimeout(cueShowTimerRef.current);
        cueShowTimerRef.current = null;
      }
    };

    const update = () => {
      rafId = null;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = Math.max(1, rect.height - vh);
      const scrolled = Math.max(0, -rect.top);
      const p = clamp01(scrolled / total);
      setProgress(p);

      // ---- Scroll cue overlay (read-only — does not affect progress) ----
      // Cue is desktop-only; mobile uses its own staged WE → WE SHAPE
      // reveal which is self-evidently a scroll story.
      if (!isDesktop) {
        if (!cueDismissedRef.current) {
          cueDismissedRef.current = true;
          clearReshow();
          setCueVisible(false);
        }
        return;
      }
      if (cueDismissedRef.current) return;

      // Reveal clearly underway → dismiss permanently.
      const splitNow = clamp01(p / 0.32);
      if (splitNow >= REVEAL_THRESHOLD) {
        cueDismissedRef.current = true;
        clearReshow();
        setCueVisible(false);
        return;
      }

      const pinned = rect.top <= 0 && rect.bottom > vh;
      if (!pinned) {
        // Section not pinned — out of scope for the cue.
        clearReshow();
        setCueVisible(false);
        return;
      }

      // Section is pinned, reveal not yet underway. While scrolling
      // (tick fires every rAF), hide the cue and keep deferring the
      // reshow timer. When scrolling stops, the last-set timer fires
      // and brings the cue back.
      setCueVisible(false);
      armReshow();
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
      clearReshow();
    };
  }, [reducedMotion, isDesktop]);

  // ---- Static fallback (reduced motion only) ----------------------
  if (reducedMotion) {
    return (
      <section
        id={id}
        className="we-shape we-shape--static"
        data-testid="we-shape-section"
        aria-label="What we shape — capabilities"
      >
        <span className="we-shape__bg we-shape__bg--we" aria-hidden="true">
          We
        </span>
        <span className="we-shape__bg we-shape__bg--shape" aria-hidden="true">
          Shape
        </span>
        <div className="we-shape__inner">
          <div className="we-shape__list" role="list">
            {CAPABILITIES.map((c) => (
              <span
                key={c.word}
                role="listitem"
                className="we-shape__item"
                data-testid={`we-shape-item-${c.testid}`}
              >
                {c.word}
              </span>
            ))}
          </div>
          <p className="we-shape__support" data-testid="we-shape-support">
            For residences, commercial spaces, staged homes, and curated
            moments of living.
          </p>
          <a
            href="/enquire"
            className="we-shape__cta"
            data-testid="we-shape-cta"
          >
            Begin a Conversation
            <svg
              width="22"
              height="10"
              viewBox="0 0 22 10"
              aria-hidden="true"
              className="we-shape__cta-arrow"
            >
              <path
                d="M1 5h20M17 1l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </section>
    );
  }

  // ---- Pinned choreography stage progress -------------------------
  // DESKTOP timeline (unchanged):
  //   0.00 – 0.32  WE/SHAPE split begins and resolves
  //   0.00 – 0.30  WE/SHAPE fade
  //   0.34 – 0.84  ARCHITECTURE → EXPERIENCES rise (per-item 0.14)
  //   0.84 – 0.91  Supporting line
  //   0.91 – 1.00  CTA
  const splitP = clamp01(progress / 0.32);
  const fadeP = clamp01(progress / 0.30);
  const archP = clamp01((progress - 0.34) / 0.14);
  const intP = clamp01((progress - 0.46) / 0.14);
  const stylP = clamp01((progress - 0.58) / 0.14);
  const expP = clamp01((progress - 0.70) / 0.14);
  const supP = clamp01((progress - 0.84) / 0.07);
  const ctaP = clamp01((progress - 0.91) / 0.09);

  // MOBILE timeline — WE SHAPE present at entry, brief hold, then
  // the rest of the reveal happens with every meaningful scroll
  // gesture (no long dead zone after the phrase is read).
  //   0.00 – 0.12  HOLD — WE SHAPE rests fully visible
  //   0.12 – 0.32  Phrase dims and fades out
  //   0.30 – 0.52  Service stack rises (per-item, overlapping)
  //   0.55 – 0.72  Supporting line fades in
  //   0.78 – 0.94  CTA fades in
  //   0.94 – 1.00  Final settled state before natural release
  const mPhraseOutP = clamp01((progress - 0.12) / 0.20);
  const mArchP = clamp01((progress - 0.30) / 0.12);
  const mIntP = clamp01((progress - 0.36) / 0.12);
  const mStylP = clamp01((progress - 0.42) / 0.12);
  const mExpP = clamp01((progress - 0.48) / 0.12);
  const mSupP = clamp01((progress - 0.55) / 0.14);
  const mCtaP = clamp01((progress - 0.78) / 0.14);

  const itemPs = [archP, intP, stylP, expP];
  const mItemPs = [mArchP, mIntP, mStylP, mExpP];

  return (
    <section
      id={id}
      className="we-shape"
      data-testid="we-shape-section"
      aria-label="What we shape — capabilities"
      style={{
        "--p-split": splitP,
        "--p-fade": fadeP,
        "--m-phrase-out": mPhraseOutP,
        "--m-sp": mSupP,
        "--m-cp": mCtaP,
      }}
    >
      <div
        ref={journeyRef}
        className="we-shape__journey"
        data-testid="services-section"
      >
        <div className="we-shape__sticky">
          {/* Phrase: flex-centered so "WE SHAPE" reads as one balanced
              statement at progress 0. Each word translates outward
              independently as split progresses. */}
          <div className="we-shape__phrase" aria-hidden="true">
            <span className="we-shape__bg we-shape__bg--we">We</span>
            <span className="we-shape__bg we-shape__bg--shape">Shape</span>
          </div>

          {/* List — separately absolutely centered, becomes the focal
              point once revealed. */}
          <ul className="we-shape__list" data-testid="we-shape-list">
            {CAPABILITIES.map((c, i) => (
              <li
                key={c.word}
                className="we-shape__item"
                style={{ "--ip": itemPs[i], "--mip": mItemPs[i] }}
                data-testid={`we-shape-item-${c.testid}`}
              >
                {c.word}
              </li>
            ))}
          </ul>

          {/* Support + CTA — anchored just below the list. */}
          <div className="we-shape__after" data-testid="conversation-section">
            <p
              className="we-shape__support"
              style={{ "--sp": supP }}
              data-testid="we-shape-support"
            >
              For residences, commercial spaces, staged homes, and curated
              moments of living.
            </p>

            <a
              href="/enquire"
              className="we-shape__cta"
              style={{ "--cp": ctaP }}
              data-testid="we-shape-cta"
            >
              Begin a Conversation
              <svg
                width="22"
                height="10"
                viewBox="0 0 22 10"
                aria-hidden="true"
                className="we-shape__cta-arrow"
              >
                <path
                  d="M1 5h20M17 1l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>

          {/* Temporary scroll cue — visual overlay only. Appears 1s after
              the user enters the WE SHAPE initial state, fades away on
              the first scroll inside the section, never returns. */}
          <div
            className={[
              "we-shape__scroll-cue",
              cueVisible ? "is-visible" : "",
            ].join(" ")}
            aria-hidden="true"
            data-testid="we-shape-scroll-cue"
          >
            <span className="we-shape__scroll-cue-text">Scroll to reveal</span>
            <span className="we-shape__scroll-cue-line" aria-hidden="true" />
            <span className="we-shape__scroll-cue-dot" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}
